import { useTranslation } from 'module/hooks/use-translation';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { Page } from 'module/ui/components/molecules/page';
import { Argon } from 'module/ui/components/templates/argon';
import {
  useAddAiPagesMutation,
  useAddWebsiteToKnowledgeBaseMutation,
  useCreateKnowledgeBaseAttachmentMutation,
  useDeleteKnowledgeBaseFolderContentMutation,
  useDeleteKnowledgeBaseFolderMutation,
  useKnowledgeBaseFolderQuery,
  useKnowledgeBaseFolderTreeQuery,
  useUpdateKnowledgeBaseFolderMutation,
} from 'queries/knowledge-base';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
import { AiPagesPayload } from 'components/organisms/knowledge-base/add-ai-pages';
import { AddAiPagesDrawer } from 'components/organisms/knowledge-base/add-ai-pages/drawer';
import {
  AddFilesPayload,
  UploadFile,
} from 'components/organisms/knowledge-base/add-files/add-files';
import { AddFilesDrawer } from 'components/organisms/knowledge-base/add-files/drawer';
import {
  AddKnowledgeTrigger,
  AddKnowledgeTriggerType,
} from 'components/organisms/knowledge-base/add-knowledge-trigger';
import {
  AddWebsiteDrawer,
  AddWebsitePayload,
} from 'components/organisms/knowledge-base/add-website';
import { NoFilesInFolder } from 'components/organisms/knowledge-base/empty-states/no-files';
import { FolderContentList } from 'components/organisms/knowledge-base/folder-content/list';
import { UploadingFiles } from 'components/organisms/knowledge-base/folder-content/uploading-files';
import { FolderTitle } from 'components/organisms/knowledge-base/folder-title';
import { useKnowledgeBaseNavigation } from 'components/organisms/knowledge-base/navigation-utils';

export const KnowledgeBaseFolderPage = () => {
  const { folderId: folderIdString } = useParams<{ folderId: string }>();
  const folderId = Number(folderIdString);
  const { data: folder, isLoading: isFolderLoading } =
    useKnowledgeBaseFolderQuery(folderId);
  const { data: folderTree, isLoading: isFolderTreeLoading } =
    useKnowledgeBaseFolderTreeQuery(folderId);
  const searchPlaceholder = useTranslation('search');
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const { mutate: updateKnowledgeBaseFolder } =
    useUpdateKnowledgeBaseFolderMutation();
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);
  const { mutateAsync: uploadFiles } =
    useCreateKnowledgeBaseAttachmentMutation();
  const { mutateAsync: addWebsiteToKnowledgeBase } =
    useAddWebsiteToKnowledgeBaseMutation();
  const { mutateAsync: addAiPagesApi } = useAddAiPagesMutation();
  const closeFileDialog = useCallback(() => {
    setShowFileDialog(false);
  }, []);
  const handleUpload = useCallback(
    async (payload: AddFilesPayload) => {
      setUploadingFiles(payload.files);
      await Promise.all(
        payload.files.map(async (file) => {
          await uploadFiles({
            folderId,
            file: file.originFileObj,
            info: {
              name: file.name,
              publicVisibility: true,
            },
          })
            .then(() => {
              setUploadingFiles((prev) =>
                prev.filter((f) => f.uid !== file.uid),
              );
            })
            .catch(() => {
              setUploadingFiles((prev) =>
                prev.filter((f) => f.uid !== file.uid),
              );
            });
        }),
      ).finally(() => {
        setTimeout(() => {
          setUploadingFiles([]);
        }, 2000);
        closeFileDialog();
      });
    },
    [uploadFiles, folderId, closeFileDialog],
  );
  const onFolderNameChange = useCallback(
    (value: string) => {
      updateKnowledgeBaseFolder({
        folderId,
        payload: {
          folderName: value,
        },
      });
    },
    [updateKnowledgeBaseFolder, folderId],
  );
  const { navigateToKnowledgeBase } = useKnowledgeBaseNavigation();
  const [showFileDialog, setShowFileDialog] = useState(false);
  const openFileDialog = useCallback(() => {
    setShowFileDialog(true);
  }, []);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [apiDocVersion, setApiDocVersion] = useState(false);
  const openWebsiteModal = useCallback(() => {
    setShowWebsiteModal(true);
  }, []);
  const closeWebsiteModal = useCallback(() => {
    setShowWebsiteModal(false);
  }, []);
  const [showAiPagesModal, setShowAiPagesModal] = useState(false);
  const openAiPagesModal = useCallback(() => {
    setShowAiPagesModal(true);
  }, []);
  const closeAiPagesModal = useCallback(() => {
    setShowAiPagesModal(false);
  }, []);
  const onAddMenuSelect = useCallback(
    (type: AddKnowledgeTriggerType) => {
      switch (type) {
        case AddKnowledgeTriggerType.ImportWebsite:
          openWebsiteModal();
          setApiDocVersion(false);
          break;
        case AddKnowledgeTriggerType.ApiDoc:
          openWebsiteModal();
          setApiDocVersion(true);
          break;
        case AddKnowledgeTriggerType.Files:
          openFileDialog();
          break;
        case AddKnowledgeTriggerType.AiPages:
          openAiPagesModal();
          break;
      }
    },
    [openFileDialog, openWebsiteModal, openAiPagesModal],
  );
  const handleAddWebsite = useCallback(
    async (payload: AddWebsitePayload) => {
      await addWebsiteToKnowledgeBase({
        folder_id: folderId,
        source_url: payload.websiteUrl,
        selected_nodes: payload.selectedFolderPaths,
        content_type: payload.apiDocVersion ? 'API_DOC' : 'WEBSITE',
      });
      closeWebsiteModal();
    },
    [addWebsiteToKnowledgeBase, closeWebsiteModal, folderId],
  );
  const handleAddAiPages = useCallback(
    async (payload: AiPagesPayload) => {
      const { filter, selectedTemplates } = payload;

      await addAiPagesApi({
        folder_id: folderId,
        templateIds: selectedTemplates,
        filter,
      });
      closeAiPagesModal();
    },
    [addAiPagesApi, closeAiPagesModal, folderId],
  );
  const { mutateAsync: deleteFolderApi } =
    useDeleteKnowledgeBaseFolderMutation();
  const { mutateAsync: deleteFileApi } =
    useDeleteKnowledgeBaseFolderContentMutation();
  const deleteFolder = useCallback(
    async (folderId: number) => {
      await deleteFolderApi({ folderId, rootFolderId: folder?.folderId });
    },
    [deleteFolderApi, folder?.folderId],
  );
  const deleteFile = useCallback(
    async (folderId: number, fileId: number) => {
      if (folder?.folderId == null) {
        return;
      }

      await deleteFileApi({
        folderId,
        contentId: fileId,
        rootFolderId: folder?.folderId,
      });
    },
    [deleteFileApi, folder?.folderId],
  );

  useEffect(() => {
    if (isFolderLoading) {
      return;
    }

    if (folder == null) {
      navigateToKnowledgeBase();
    }
  }, [folder, isFolderLoading, navigateToKnowledgeBase]);
  const isFolderTreeEmpty =
    (folderTree?.contents ?? []).length === 0 &&
    (folderTree?.children ?? []).length === 0;
  const isEmpty = isFolderTreeEmpty && uploadingFiles.length === 0;

  if (isFolderLoading || isFolderTreeLoading) {
    return <PageLoading />;
  }

  return folder == null || folderTree == null ? (
    <PageLoading />
  ) : (
    <Argon
      breadcrumbs={
        <>
          <Breadcrumb.Item>
            <Link to={`/settings/${ROUTE_PATHS.KNOWLEDGE_BASE}`}>
              <FormattedMessage id="knowledge_base.page.title" />
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>&nbsp;</Breadcrumb.Item>
        </>
      }
      title={
        <FolderTitle
          value={folder.folderName}
          onChange={onFolderNameChange}
          renderTitle={(value) => (
            <Page.Header.Title>{value}</Page.Header.Title>
          )}
        />
      }
      content={
        <>
          {isEmpty ? (
            <NoFilesInFolder
              primaryAction={<AddKnowledgeTrigger onSelect={onAddMenuSelect} />}
            />
          ) : (
            <>
              {uploadingFiles.length > 0 && (
                <UploadingFiles files={uploadingFiles} />
              )}
              {!isFolderTreeEmpty && (
                <FolderContentList
                  knowledgeBase={folder}
                  folderTree={folderTree}
                  deleteFolder={deleteFolder}
                  deleteFile={deleteFile}
                />
              )}
            </>
          )}
          <AddFilesDrawer
            open={showFileDialog}
            folderName={folder.folderName}
            onClose={closeFileDialog}
            onDone={handleUpload}
          />
          <AddWebsiteDrawer
            open={showWebsiteModal}
            apiDocVersion={apiDocVersion}
            folderName={folder.folderName}
            onClose={closeWebsiteModal}
            onDone={handleAddWebsite}
          />
          <AddAiPagesDrawer
            open={showAiPagesModal}
            folderName={folder.folderName}
            onClose={closeAiPagesModal}
            onDone={handleAddAiPages}
          />
        </>
      }
      actions={{
        secondary: (
          <CenterAlignedBox>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
              placeholder={searchPlaceholder}
            />
            <AddKnowledgeTrigger onSelect={onAddMenuSelect} />
          </CenterAlignedBox>
        ),
      }}
    />
  );
};
